import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Loader2, CheckCircle2, History } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useStoreAllocations, type AllocationStatus, type StoreAllocation } from "@/features/store-allocations/api";
import { MarkConsumedDialog } from "@/features/store-allocations/MarkConsumedDialog";
import { AllocationTimelineDrawer } from "@/features/store-allocations/AllocationTimelineDrawer";
import { resolveLocationPath, type LocationNode } from "@/features/store-indents/LocationHierarchyPicker";
import { useStoreLookups } from "@/features/store-indents/api";
import { formatDate } from "@/features/assets/format";


export const Route = createFileRoute("/_authenticated/my-allocations")({
  component: MyAllocationsPage,
});

type TabKey = "active" | "Fully Consumed" | "all";

function statusVariant(s: AllocationStatus) {
  if (s === "Fully Consumed") return "default" as const;
  if (s === "Partially Consumed") return "secondary" as const;
  if (s === "Returned") return "outline" as const;
  return "secondary" as const;
}

function MyAllocationsPage() {
  const [tab, setTab] = useState<TabKey>("active");
  const { data = [], isLoading } = useStoreAllocations({ status: tab });
  const { data: lookups } = useStoreLookups();
  const locations = (lookups?.locations ?? []) as LocationNode[];
  const [markTarget, setMarkTarget] = useState<StoreAllocation | null>(null);
  const [drawer, setDrawer] = useState<StoreAllocation | null>(null);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">My Allocations</h1>
        <p className="text-sm text-muted-foreground">
          Items your department has received from stores. Mark them as consumed once they're used up.
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="Fully Consumed">Fully Consumed</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card><CardContent className="p-0">
        {isLoading ? (
          <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : data.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">No allocations in this view.</div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Indent</TableHead>
              <TableHead>Item</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead className="text-right">Allocated</TableHead>
              <TableHead className="text-right">Consumed</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Ordered</TableHead>
              <TableHead>Allocated On</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.map((a) => {
                const canMark = a.status !== "Fully Consumed" && a.status !== "Returned" && a.kind !== "asset";
                return (
                  <TableRow key={a.id}>
                    <TableCell className="font-mono text-xs">
                      {a.indent?.store_indent_no ? (
                        <Link
                          to="/store-indents/$id"
                          params={{ id: a.store_indent_id }}
                          className="text-primary hover:underline"
                        >
                          {a.indent.store_indent_no}
                        </Link>
                      ) : "—"}
                    </TableCell>
                    <TableCell>{a.spare?.name || a.asset?.name || a.item_name || "—"}</TableCell>
                    <TableCell><Badge variant="outline">{a.kind}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums">{Number(a.qty_allocated)}</TableCell>
                    <TableCell className="text-right tabular-nums">{Number(a.qty_consumed)}</TableCell>
                    <TableCell className="text-xs">{resolveLocationPath(locations, a.location_id)}</TableCell>
                    <TableCell className="text-xs">{a.ordered_at ? formatDate(a.ordered_at) : "—"}</TableCell>
                    <TableCell className="text-xs">{formatDate(a.allocated_at)}</TableCell>
                    <TableCell><Badge variant={statusVariant(a.status)}>{a.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setDrawer(a)}>
                          <History className="h-3.5 w-3.5" />
                        </Button>
                        {canMark && (
                          <Button size="sm" variant="outline" onClick={() => setMarkTarget(a)}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Mark Consumed
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      <MarkConsumedDialog
        open={!!markTarget}
        onOpenChange={(v) => { if (!v) setMarkTarget(null); }}
        allocation={markTarget}
      />
      <AllocationTimelineDrawer
        allocation={drawer}
        onOpenChange={(v) => { if (!v) setDrawer(null); }}
      />
    </div>
  );
}
